<script>
  import { authModule } from "@/stores/auth.js";
  import { useAutoLogout } from "@/stores/autoLogout.js";
  import { themeModule } from "@/stores/theme.js";
  import { mapStores } from 'pinia'

  export default {
    name: "topbar",
    computed: {
      ...mapStores(authModule, themeModule, useAutoLogout),
      userName() {
        return this.authStore.isLoggedIn ? this.authStore.userObj?.name : 'No User'
      },
      accountType() {
        return this.authStore.userObj?.account_type || ''
      },
      isDark() {
        return this.themeStore.themeObj.theme === 'dark'
      }
    },
    methods: {
      toggleTheme() {
        this.themeStore.setTheme(this.isDark ? 'light' : 'dark')
      },
      logout() {
        // Through the store, so the expiry timers are cleared too: left running,
        // they would fire against a session that is already gone and stamp a
        // spurious "session expired" on the next sign-in.
        this.autoLogoutStore.logout('manual', { preserveRedirect: false })
      }
    }
  }
</script>

<template>
  <div class="navbar-custom">
    <div class="topbar container-fluid">
      <div class="d-flex align-items-center gap-lg-2 gap-1">

        <!-- Topbar Brand Logo -->
        <div class="logo-topbar">
          <!-- Hyper swaps these two: .logo-light shows on a dark topbar, .logo-dark on a light one. -->
          <router-link to="/" class="logo-light">
            <span class="logo-lg">
              <img src="@/assets/brand/logo-light.png" alt="DNA Express Logistics">
            </span>
            <span class="logo-sm">
              <img src="@/assets/brand/logo-sm-light.png" alt="DNA Express">
            </span>
          </router-link>
          <router-link to="/" class="logo-dark">
            <span class="logo-lg">
              <img src="@/assets/brand/logo-dark.png" alt="DNA Express Logistics">
            </span>
            <span class="logo-sm">
              <img src="@/assets/brand/logo-sm-dark.png" alt="DNA Express">
            </span>
          </router-link>
        </div>

        <!-- Horizontal Menu Toggle Button -->
        <button class="navbar-toggle" data-bs-toggle="collapse" data-bs-target="#topnav-menu-content">
          <div class="lines">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>

      <ul class="topbar-menu d-flex align-items-center gap-3">
        <li class="d-none d-sm-inline-block">
          <!-- Static label: the tooltip directive binds its title once on mount. -->
          <div class="nav-link" id="light-dark-mode" role="button" @click="toggleTheme" v-tooltip="'Toggle dark mode'">
            <i class="mdi font-22" :class="isDark ? 'mdi-white-balance-sunny' : 'mdi-weather-night'"></i>
          </div>
        </li>

        <li class="dropdown">
          <a class="nav-link dropdown-toggle arrow-none nav-user px-2" data-bs-toggle="dropdown" href="#" role="button" aria-haspopup="false" aria-expanded="false">
            <span class="account-user-avatar">
              <i class="mdi mdi-account-circle font-24"></i>
            </span>
            <span class="d-lg-flex flex-column gap-1 d-none">
              <h5 class="my-0">{{ userName }}</h5>
              <h6 class="my-0 fw-normal">{{ accountType }}</h6>
            </span>
          </a>
          <div class="dropdown-menu dropdown-menu-end dropdown-menu-animated profile-dropdown">
            <div class="dropdown-header noti-title">
              <h6 class="text-overflow m-0">Welcome, {{ userName }}!</h6>
            </div>

            <router-link to="/admin/settings" class="dropdown-item">
              <i class="mdi mdi-cog-outline font-16 me-1"></i>
              <span>Settings</span>
            </router-link>

            <!-- A plain anchor, not a router-link: the handbook is static content in
                 public/, so it has no route. A router-link would hand this path to the
                 router and land on the 404 page.
                 index.html is named explicitly rather than linking the directory: vite
                 dev answers a bare /handbook/ with the SPA shell, and naming the file
                 needs no directory-index support from whatever serves it in production. -->
            <a href="/handbook/index.html" target="_blank" rel="noopener" class="dropdown-item d-flex align-items-center">
              <i class="mdi mdi-book-open-page-variant-outline font-16 me-1"></i>
              <span>Handbook</span>
              <i class="mdi mdi-open-in-new font-13 ms-auto text-muted"></i>
              <span class="visually-hidden">(opens in a new tab)</span>
            </a>

            <a href="javascript:void(0);" class="dropdown-item" @click="logout()">
              <i class="mdi mdi-logout font-16 me-1"></i>
              <span>Logout</span>
            </a>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
/* Hyper sizes its own compact mark at 20px; this wordmark is far wider than
   tall and is unreadable at that height. */
.logo-topbar :deep(.logo-lg img) {
  height: 34px;
}
</style>
